import { useEffect, useState, useCallback } from "react";
import {
  api,
  DEMO_ROOM_ID,
  type RoomStatus,
  type RosterRow,
  type SignedInUser,
} from "../lib/api";

const POLL_MS = 2000;

export function ClassroomScreen({ user }: { user: SignedInUser }): JSX.Element {
  const [status, setStatus] = useState<RoomStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyRow, setBusyRow] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await api.roomStatus(DEMO_ROOM_ID);
      setStatus(next);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const t = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(t);
  }, [refresh]);

  async function handleStart(): Promise<void> {
    setBusy(true);
    try {
      await api.startSession(DEMO_ROOM_ID, 40);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function handleEnd(): Promise<void> {
    if (!status?.session) return;
    setBusy(true);
    try {
      await api.endSession(status.session.sessionId);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function handleSimulateTap(studentId: string): Promise<void> {
    setBusyRow(studentId);
    setError(null);
    try {
      await api.simulateTapIn(DEMO_ROOM_ID, studentId);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyRow(null);
    }
  }

  async function handleEmergencyUnlock(studentId: string): Promise<void> {
    if (!status?.session) return;
    setBusyRow(studentId);
    setError(null);
    try {
      await api.emergencyUnlock(status.session.sessionId, studentId, "manual");
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyRow(null);
    }
  }

  async function handleResetDemo(): Promise<void> {
    const ok = window.confirm(
      "Reset the demo? This wipes the active session, every student check-in, and the event log for this school. Users + rooms remain.",
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await api.resetDemo();
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!status) {
    return (
      <section className="room-card">
        <p>{error ?? "Loading roster…"}</p>
      </section>
    );
  }

  const session = status.session;
  const remaining = session ? formatRemaining(session.endTime) : null;
  const lockedCount = status.counts.LOCKED ?? 0;
  const violations = status.counts.VIOLATION ?? 0;
  const teacherUnlocks = status.counts.UNLOCKED_TEACHER ?? 0;
  const notCheckedIn = status.counts.NOT_CHECKED_IN ?? 0;

  return (
    <section className="room-card">
      <div className="room-head">
        <div>
          <p className="eyebrow">Period 3 · {status.room.name}</p>
          <h1>{user.displayName}</h1>
          <p className="lede">
            {session
              ? `Session active · ${remaining} remaining (auto-unlock at bell)`
              : "No active session."}
          </p>
        </div>
        <div className="counters">
          <div>
            <span className="counter-num">{lockedCount}</span>
            <span className="counter-lbl">Locked</span>
          </div>
          <div>
            <span className="counter-num">{notCheckedIn}</span>
            <span className="counter-lbl">Not in</span>
          </div>
          <div>
            <span className="counter-num">{violations}</span>
            <span className="counter-lbl">Violations</span>
          </div>
          <div>
            <span className="counter-num">{teacherUnlocks}</span>
            <span className="counter-lbl">Unlocked</span>
          </div>
        </div>
      </div>

      <div className="cta-row">
        {session ? (
          <button className="btn-primary danger" disabled={busy} onClick={() => void handleEnd()}>
            End class · unlock all
          </button>
        ) : (
          <button className="btn-primary" disabled={busy} onClick={() => void handleStart()}>
            Start 40-min class
          </button>
        )}
        {user.role === "ADMIN" && (
          <button
            className="btn-secondary"
            disabled={busy}
            onClick={() => void handleResetDemo()}
            title="Wipes session, check-ins, and event log for this school"
          >
            Reset demo
          </button>
        )}
      </div>

      <ul className="roster">
        {status.roster.map((s) => (
          <RosterRowView
            key={s.studentId}
            row={s}
            sessionActive={!!session}
            busy={busyRow === s.studentId}
            onSimulateTap={() => void handleSimulateTap(s.studentId)}
            onEmergencyUnlock={() => void handleEmergencyUnlock(s.studentId)}
          />
        ))}
      </ul>

      {error && <p className="error" style={{ marginTop: "1rem" }}>{error}</p>}
    </section>
  );
}

function RosterRowView({
  row,
  sessionActive,
  busy,
  onSimulateTap,
  onEmergencyUnlock,
}: {
  row: RosterRow;
  sessionActive: boolean;
  busy: boolean;
  onSimulateTap: () => void;
  onEmergencyUnlock: () => void;
}): JSX.Element {
  // Day 8.3: when state=LOCKED but the OS-level guard isn't actually
  // enforcing (heartbeat stale or guardEnabled=false), show the tamper
  // badge instead of "✅ Locked" so Sam doesn't trust a green roster
  // when a phone is exploitable.
  const tampered =
    row.state === "LOCKED" &&
    (row.kioskHealth === "disabled" ||
      row.kioskHealth === "stale" ||
      row.kioskHealth === "different_device");
  return (
    <li className={`row row-${row.state.toLowerCase()}${tampered ? " row-tampered" : ""}`}>
      <span className="row-name">
        {row.displayName}
        {row.violationCount > 0 && (
          <span
            className="row-violation"
            title={`${row.violationCount} violation${row.violationCount === 1 ? "" : "s"} this session`}
          >
            ⚠ {row.violationCount}
          </span>
        )}
      </span>
      <span className="row-state" title={tampered ? tamperTitle(row) : undefined}>
        {busy ? "…" : tampered ? tamperLabel(row.kioskHealth) : labelFor(row.state)}
      </span>
      <span className="row-actions">
        {sessionActive && (row.state === "NOT_CHECKED_IN" || row.state === "UNLOCKED_TEACHER") && (
          <button className="row-btn" disabled={busy} onClick={onSimulateTap}>
            {busy ? "…" : row.state === "UNLOCKED_TEACHER" ? "Re-lock (sim)" : "Tap-in (sim)"}
          </button>
        )}
        {sessionActive && row.state === "LOCKED" && (
          <button className="row-btn row-btn-warn" disabled={busy} onClick={onEmergencyUnlock}>
            {busy ? "…" : "Emergency"}
          </button>
        )}
      </span>
    </li>
  );
}

function labelFor(s: RosterRow["state"]): string {
  switch (s) {
    case "NOT_CHECKED_IN":
      return "○ Not in";
    case "LOCKED":
      return "✅ Locked";
    case "UNLOCKED_AUTO":
      return "🔓 Bell";
    case "UNLOCKED_TEACHER":
      return "🔓 Released";
    case "VIOLATION":
      return "❗ Violation";
    case "OFFLINE":
      return "⚠️ Offline";
  }
}

function tamperLabel(h: RosterRow["kioskHealth"]): string {
  if (h === "disabled") return "🚫 OS not enforcing";
  if (h === "different_device") return "🚫 Different device";
  return "🚫 No heartbeat";
}

function tamperTitle(row: RosterRow): string {
  const seconds = row.lastKioskHealthAt
    ? Math.floor((Date.now() - row.lastKioskHealthAt) / 1000)
    : null;
  if (row.kioskHealth === "disabled") {
    return "Device reports LockerEduGuard is disabled. Audit log will show a VIOLATION.";
  }
  if (row.kioskHealth === "different_device") {
    return "The phone heartbeating now isn't the one this student tapped in with. Possible device-share — investigate after class.";
  }
  return `No heartbeat${seconds == null ? "" : ` for ${seconds}s`} — the OS guard may have crashed or the phone is offline.`;
}

function formatRemaining(endTimeIso: string): string {
  const ms = new Date(endTimeIso).getTime() - Date.now();
  if (ms <= 0) return "0:00";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
